import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useSession } from 'next-auth/react';

import BackendService from '../services/backendService';
import { Template } from '../services/TemplateService/Template.type';

function usePublicTemplate({
  templateId,
  templateTenantId,
  isTemplateOwner,
  isFetchingRawTemplate,
}: {
  templateId?: string | null;
  templateTenantId?: string;
  isTemplateOwner: boolean | undefined;
  isFetchingRawTemplate?: boolean;
}) {
  const templateCacheKey = ['publicTemplate', templateId, templateTenantId];
  const session = useSession();

  const { data: template, isFetching: isFetchingTemplate, error, isError } = useQuery<
    Template | undefined,
    AxiosError
  >(
    templateCacheKey,
    async _ => {
      if (templateId) {
        if (templateTenantId) {
          if (
            session.status === 'authenticated' &&
            session.data?.user &&
            isTemplateOwner
          ) {
            return BackendService.instance.blueprint.retrieveTemplate(
              templateId,
            ) as Promise<Template | undefined>;
          } else {
            return BackendService.instance.blueprint.retriveTemplateUnauthenticated(
              templateTenantId,
              templateId,
            ) as Promise<Template | undefined>;
          }
        }
      }
      return Promise.resolve(undefined);
    },
    {
      enabled: Boolean(
        templateId && templateTenantId && !isTemplateOwner && !isFetchingRawTemplate,
      ),
      staleTime: 1000 * 60 * 5,
      retry: (count: number, error) => {
        // due to SSR fetching we dont want to retry on 403s and 404s
        if (error.response?.status && [403, 404].includes(error.response?.status)) {
          return false;
        }

        // Default retry is 3 times
        if (count > 3) {
          return false;
        }
        return true;
      },
    },
  );

  return {
    template,
    isFetchingTemplate,
    error: error?.response?.status,
    isError,
  };
}
export default usePublicTemplate;
